'use client';

import {
  Alert,
  Box,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { EonSparkVersion } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';

import { KeyValuePairEditor } from '../../../components/KeyValuePairEditor';
import { eonSparkVersionLabelLong } from '../../../eonSparkVersionUtils';
import type { CreateJobTemplateWizardState } from '../createJobTemplateWizardState';

export const CreateJobTemplateStep = (
  props: StepProps<CreateJobTemplateWizardState>
) => {
  const dal = useDAL();
  const { wizardState: state, setWizardState } = props;
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = (partial: Partial<CreateJobTemplateWizardState>) =>
    setWizardState({ ...state, ...partial });

  const isValidS3Url = /^s3[a]?:\/\/.+/.test(state.entryPoint.trim());

  const hasValidEonSparkVersions =
    !state.eonSparkVersionsEnabled || state.eonSparkVersions.length > 0;

  const canCreate =
    state.name.trim().length >= 3 &&
    isValidS3Url &&
    hasValidEonSparkVersions &&
    !creating;

  const handleCreate = async () => {
    setError(null);
    setCreating(true);
    try {
      const args = state.entryPointArguments
        .trim()
        .split(/\s+/)
        .filter((arg) => arg.length > 0);

      const sparkConfig = Object.fromEntries(
        state.sparkConfig
          .filter((p) => p.key.trim() && p.value.trim())
          .map((p) => [p.key.trim(), p.value.trim()])
      );

      const envVars = Object.fromEntries(
        state.environmentVariables
          .filter((p) => p.key.trim() && p.value.trim())
          .map((p) => [p.key.trim(), p.value.trim()])
      );

      const requestBody = {
        name: state.name.trim(),
        ...(state.description.trim() && {
          description: state.description.trim(),
        }),
        ...(state.jobTimeout && {
          jobTimeout: parseInt(state.jobTimeout, 10),
        }),
        ...(state.retryEnabled && {
          retryPolicy: {
            maxAttempts: state.maxRetries,
            cooldownInterval: state.cooldownInterval,
          },
        }),
        sparkSubmitParams: {
          entryPoint: state.entryPoint.trim(),
          ...(args.length > 0 && { entryPointArguments: args }),
          ...(Object.keys(sparkConfig).length > 0 && {
            sparkSubmitParameters: sparkConfig,
          }),
          ...(Object.keys(envVars).length > 0 && {
            environmentVariables: envVars,
          }),
        },
        ...((state.driverDiskSize || state.executorDiskSize) && {
          storageConfig: {
            ...(state.driverDiskSize && {
              driverDiskSize: parseInt(state.driverDiskSize, 10),
            }),
            ...(state.executorDiskSize && {
              executorDiskSize: parseInt(state.executorDiskSize, 10),
            }),
          },
        }),
        ...(state.eonSparkVersions.length > 0 && {
          eonSparkVersions: state.eonSparkVersions,
        }),
      };

      if (state.isUpdateMode) {
        await dal.lakehouse.updateJobTemplate(
          state.workspaceId,
          state.templateId,
          {
            ...requestBody,
            // Explicitly null cleared fields so the backend doesn't treat omission as "no change"
            ...(!state.description.trim() && { description: null }),
            ...(!state.jobTimeout && { jobTimeout: null }),
            ...(!state.retryEnabled && { retryPolicy: null }),
            ...(!state.eonSparkVersionsEnabled && { eonSparkVersions: [] }),
          }
        );
        void props.finish('Job template updated successfully');
      } else {
        await dal.lakehouse.createJobTemplate(state.workspaceId, requestBody);
        void props.finish('Job template created successfully');
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : `Failed to ${state.isUpdateMode ? 'update' : 'create'} job template`
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <StepContainer
      hideStepper
      stepperIndex={0}
      canGoNext={() => canCreate}
      nextButtonText={
        creating
          ? state.isUpdateMode
            ? 'Updating...'
            : 'Creating...'
          : state.isUpdateMode
            ? 'Update'
            : 'Create'
      }
      nextButtonLoadingText={state.isUpdateMode ? 'Updating...' : 'Creating...'}
      backButtonText='Cancel'
      onBackClick={props.back}
      onNextClick={handleCreate}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Stack gap='min(60px, 4vw)' divider={<Divider />}>
        {/* Details */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Details</Typography>
              <Typography variant='body2' color='text.secondary'>
                Name and describe the job template for easy identification.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Name</Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder='Enter template name'
                value={state.name}
                onChange={(e) => update({ name: e.target.value })}
                error={state.name.length > 0 && state.name.trim().length < 3}
                fullWidth
              />
              {state.name.length > 0 && state.name.trim().length < 3 && (
                <FormHelperText error>
                  Name must be at least 3 characters
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Description</Typography>
              </FormLabel>
              <TextField
                size='small'
                placeholder='Enter description (optional)'
                value={state.description}
                onChange={(e) => update({ description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Spark Application */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Spark Application</Typography>
              <Typography variant='body2' color='text.secondary'>
                Specify the application entry point and any arguments to pass to
                it.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Entry Point</Typography>
              </FormLabel>
              <FormHelperText sx={{ mx: 0, mt: 0, mb: '8px' }}>
                S3 path to your Spark application JAR or Python script
              </FormHelperText>
              <TextField
                size='small'
                placeholder='s3://bucket/path/to/application.jar'
                value={state.entryPoint}
                onChange={(e) => update({ entryPoint: e.target.value })}
                error={state.entryPoint.length > 0 && !isValidS3Url}
                fullWidth
              />
              {state.entryPoint.length > 0 && !isValidS3Url && (
                <FormHelperText error>
                  Entry point must be a valid S3 URL (s3:// or s3a://)
                </FormHelperText>
              )}
            </FormControl>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>
                  Entry Point Arguments
                </Typography>
              </FormLabel>
              <FormHelperText sx={{ mx: 0, mt: 0, mb: '8px' }}>
                Space-separated arguments to pass to the entry point
              </FormHelperText>
              <TextField
                size='small'
                placeholder='arg1 arg2 arg3'
                value={state.entryPointArguments}
                onChange={(e) =>
                  update({ entryPointArguments: e.target.value })
                }
                fullWidth
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Spark Configuration */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Spark Configuration</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define Spark configuration properties as key-value pairs.
              </Typography>
            </Stack>

            <KeyValuePairEditor
              pairs={state.sparkConfig}
              onChange={(sparkConfig) => update({ sparkConfig })}
              keyPlaceholder='spark.executor.memory'
              valuePlaceholder='4g'
            />
          </Stack>
        </Box>

        {/* Storage */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Storage</Typography>
              <Typography variant='body2' color='text.secondary'>
                Configure disk sizes for the driver and executor pods.
              </Typography>
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>
                    Driver Disk Size (GB)
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  placeholder='Default'
                  value={state.driverDiskSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      update({ driverDiskSize: val });
                    }
                  }}
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth>
                <FormLabel>
                  <Typography variant='subtitle1'>
                    Executor Disk Size (GB)
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  placeholder='Default'
                  value={state.executorDiskSize}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d+$/.test(val)) {
                      update({ executorDiskSize: val });
                    }
                  }}
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        {/* Environment Variables */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Environment Variables</Typography>
              <Typography variant='body2' color='text.secondary'>
                Define environment variables to set when running the Spark
                application.
              </Typography>
            </Stack>

            <KeyValuePairEditor
              pairs={state.environmentVariables}
              onChange={(environmentVariables) =>
                update({ environmentVariables })
              }
              keyPlaceholder='MY_VAR'
              valuePlaceholder='my_value'
            />
          </Stack>
        </Box>

        {/* Retry Policy */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='space-between'
            >
              <Stack gap='8px'>
                <Typography variant='h6'>Retry Policy</Typography>
                <Typography variant='body2' color='text.secondary'>
                  Automatically retry the job on failure with a cooldown
                  interval between attempts.
                </Typography>
              </Stack>
              <Switch
                checked={state.retryEnabled}
                onChange={(e) => update({ retryEnabled: e.target.checked })}
              />
            </Stack>

            <Stack direction='row' gap='16px'>
              <FormControl fullWidth disabled={!state.retryEnabled}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Max Retries
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.retryEnabled}
                  value={state.retryEnabled ? state.maxRetries : ''}
                  onChange={(e) =>
                    update({
                      maxRetries: Math.max(1, parseInt(e.target.value) || 1),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>

              <FormControl fullWidth disabled={!state.retryEnabled}>
                <FormLabel>
                  <Typography variant='subtitle1' color='inherit'>
                    Cooldown Interval (seconds)
                  </Typography>
                </FormLabel>
                <TextField
                  size='small'
                  type='number'
                  disabled={!state.retryEnabled}
                  value={state.retryEnabled ? state.cooldownInterval : ''}
                  onChange={(e) =>
                    update({
                      cooldownInterval: Math.max(
                        1,
                        parseInt(e.target.value) || 1
                      ),
                    })
                  }
                  slotProps={{ htmlInput: { min: 1 } }}
                  fullWidth
                />
              </FormControl>
            </Stack>
          </Stack>
        </Box>

        {/* Job Timeout */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>Job Timeout</Typography>
              <Typography variant='body2' color='text.secondary'>
                Set the maximum time the job is allowed to run before it is
                terminated.
              </Typography>
            </Stack>

            <FormControl fullWidth>
              <FormLabel>
                <Typography variant='subtitle1'>Timeout (seconds)</Typography>
              </FormLabel>
              <TextField
                size='small'
                type='number'
                placeholder='No timeout'
                value={state.jobTimeout}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val === '' || /^\d+$/.test(val)) {
                    update({ jobTimeout: val });
                  }
                }}
                slotProps={{ htmlInput: { min: 1 } }}
                fullWidth
              />
            </FormControl>
          </Stack>
        </Box>

        {/* Eon Spark Version Compatibility */}
        <Box paddingX='min(100px, 6vw)'>
          <Stack gap='24px' maxWidth='540px'>
            <Stack gap='8px'>
              <Typography variant='h6'>
                Eon Spark Version Compatibility
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Restrict this template to specific Eon Spark versions.
              </Typography>
            </Stack>

            <Stack gap='12px'>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!state.eonSparkVersionsEnabled}
                    onChange={(e) =>
                      update({
                        eonSparkVersionsEnabled: !e.target.checked,
                        ...(e.target.checked && { eonSparkVersions: [] }),
                      })
                    }
                    size='small'
                  />
                }
                label='All Versions'
              />

              <Divider />

              <Stack gap='4px'>
                {Object.values(EonSparkVersion).map((version) => (
                  <FormControlLabel
                    key={version}
                    disabled={!state.eonSparkVersionsEnabled}
                    control={
                      <Checkbox
                        checked={state.eonSparkVersions.includes(version)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            update({
                              eonSparkVersions: [
                                ...state.eonSparkVersions,
                                version,
                              ],
                            });
                          } else {
                            update({
                              eonSparkVersions: state.eonSparkVersions.filter(
                                (v) => v !== version
                              ),
                            });
                          }
                        }}
                        size='small'
                      />
                    }
                    label={eonSparkVersionLabelLong(version)}
                  />
                ))}
              </Stack>
            </Stack>
          </Stack>
        </Box>
      </Stack>

      {error && (
        <Box paddingX='min(100px, 6vw)' paddingTop='24px'>
          <Alert severity='error' sx={{ maxWidth: '540px' }}>
            <Typography variant='body2'>{error}</Typography>
          </Alert>
        </Box>
      )}
    </StepContainer>
  );
};
